/* eslint-disable no-param-reassign */
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import {
	getCreditOptionsService,
	getManageSubscriptionLinkService,
	getOneTimePaymentLinkService,
	getProratedSubscriptionPriceService,
	getSubscriptionPaymentLinkService,
	getSubscriptionPlansService,
	validateSubscriptionCouponCodeService,
} from 'api/services/CreditService';
import {
	generateApiKey,
	getActivePlansService,
	getListPromptsHistory,
	getPublicProfile,
	getStudioGenerationHistoryService,
	getUserPromptHistoryService,
	resetApiKey,
	unpublishPrompt,
} from 'api/services/ProfileService';
import { INVALID_COUPON_CODE_MESSAGE, RENDERNET_PRICING_URL } from 'core/constants/constants';
import { ApiStage } from 'core/constants/enums/ApiStages.ts';
import { AuthenticationStatus } from 'core/constants/enums/AuthenticationStatus.ts';
import { HomeTabs } from 'core/constants/enums/HomeTabs.ts';
import { PreloadChannels } from 'core/constants/enums/PreloadChannels.ts';
import { PricingModalSource } from 'core/constants/enums/PricingModalSource.ts';
import { RendernetErrors } from 'core/constants/enums/RendernetErrors.ts';
import { ToastType } from 'core/constants/enums/ToastTypeEnum.ts';
import { sendToast } from 'core/layouts/components/toast/ToastUtils';
import { runGenerateCanvasMediaAsync } from 'core/store/reducers/canvasReducer/CanvasSlice';
import { generateCharacterAsync } from 'core/store/reducers/characterStudioReducer/CharaterStudioSlice';
import {
	getMusicVideoAsync,
	musicVideoUpdateScenesAsync,
} from 'core/store/reducers/intermediateMediaReducer/IntermediateMediaSlice';
import { runGenerateStudioMediaAsync } from 'core/store/reducers/studioReducer/StudioSlice';
import { calculatePageNumbers, handleSomethingWentWrongErrorWithToast } from 'core/utils/utils';

export const getPublicProfileAsync = createAsyncThunk(
	'profile/getPublicProfileAsync',
	async (user) => getPublicProfile(user),
);

export const getUserPromptHistoryAsync = createAsyncThunk(
	'profile/getUserPromptHistoryAsync',
	async (pageNumber) => getUserPromptHistoryService(pageNumber),
);

export const getStudioHistoryAsync = createAsyncThunk(
	'profile/getStudioHistoryAsync',
	async (pageNumber) => getStudioGenerationHistoryService(pageNumber),
);

export const getActivePlansAsync = createAsyncThunk('profile/getActivePlansAsync', async () =>
	getActivePlansService(),
);

export const getListPromptsHistoryAsync = createAsyncThunk(
	'profile/getListPromptsHistoryAsync',
	async ({ page, filterValue }) => getListPromptsHistory(page, filterValue),
);

export const unpublishPromptAsync = createAsyncThunk(
	'profile/unpublishPromptAsync',
	async (promptId) => unpublishPrompt(promptId),
);

export const getCreditOptions = createAsyncThunk('profile/getCreditOptions', async () =>
	getCreditOptionsService(),
);
export const getOneTimePayLinkAsync = createAsyncThunk(
	'profile/getOneTimePayLinkAsync',
	async ({ value, successUrl, cancelUrl }) =>
		getOneTimePaymentLinkService({ value, successUrl, cancelUrl }),
);

export const getSubscriptionPlans = createAsyncThunk(
	'profile/getSubscriptionPlans',
	async (_, { dispatch }) => getSubscriptionPlansService({ dispatch }),
);
export const getSubscriptionPayLinkAsync = createAsyncThunk(
	'profile/getSubscriptionPayLinkAsync',
	async ({ price, subscriptionType, successUrl, cancelUrl }) =>
		getSubscriptionPaymentLinkService(price, subscriptionType, successUrl, cancelUrl),
);
export const getSubscriptionProratedPriceAsync = createAsyncThunk(
	'profile/getProratedPriceAsync',
	async ({ price, subscriptionType }) =>
		getProratedSubscriptionPriceService(price, subscriptionType),
);
export const validateAndApplyCouponCodeAsync = createAsyncThunk(
	'profile/validateCouponCodeAsync',
	async (couponCode) => validateSubscriptionCouponCodeService(couponCode),
);

export const getManageSubscriptionsLinkAsync = createAsyncThunk(
	'profile/getManageSubscriptionsLinkAsync',
	async (returnUrl) => getManageSubscriptionLinkService(returnUrl),
);

export const generateAPIKeyAsync = createAsyncThunk(
	'profile/getAPIKeyAsync',
	async (_, { dispatch }) => generateApiKey({ dispatch }),
);

export const resetAPIKeyAsync = createAsyncThunk(
	'profile/resetAPIKeyAsync',
	async (_, { dispatch }) => resetApiKey({ dispatch }),
);

const initialState = {
	openedMobilePricingPlans: [],
	openSubscriptionModal: { open: false, from: PricingModalSource.DEFAULT },
	userNotFound: false,
	creditOptions: [],
	getCreditOptionsApiStage: ApiStage.IDLE,

	generateError: null,
	subscriptionPlans: {
		monthly: [],
		annually: [],
	},
	getSubscriptionPlansApiStage: ApiStage.IDLE,

	getPayLinkAPIStage: ApiStage.IDLE,
	getPayLinkAPIError: null,

	openProratedPricePopUp: false,
	proratedPrice: {},
	getProratedPriceApiStage: ApiStage.IDLE,

	getManageSubscriptionsLinkAPIStage: ApiStage.IDLE,

	getPublicProfileAPIStage: ApiStage.IDLE,
	publicProfileDetails: {
		bio: '',
		blocked: false,
		credits: {
			balance: 0,
			other: 0,
			subscription: 0,
		},
		email: '',
		handle: '',
		links: {
			discord: '',
			facebook: '',
			instagram: '',
			others_1: '',
			others_2: '',
			patreon: '',
			reddit: '',
			telegram: '',
			x: '',
		},
		name: '',
		profile_url: '',
	},

	currentProfileTab: HomeTabs.PROMPTS,

	getStudioHistoryAPIStage: ApiStage.IDLE,
	studioHistory: [],
	studioHistoryTotalPages: 1,
	studioHistoryCurrentPage: 1,

	getActivePlansAPIStage: ApiStage.IDLE,
	activePlans: [],

	getUserPromptHistoryAPIStage: ApiStage.IDLE,
	userPromptHistory: [],
	userPromptHistoryTotalPages: 1,
	userPromptHistoryCurrentPage: 1,

	getListPromptsHistoryAPIStage: ApiStage.IDLE,
	listPromptsHistory: [],
	listPromptHistoryTotalPages: 1,
	listPromptHistoryCurrentPage: 1,

	getUnpublishPromptAPIStage: ApiStage.IDLE,
	unpublishPromptConfirmationVisibility: {},

	isUserAuthenticated: AuthenticationStatus.LOADING,

	getAPIKeyAPIStage: ApiStage.IDLE,
	apiKey: null,

	showResetAPIKeyModal: false,
	showAPISuccessModal: false,

	resetAPIKeyStage: ApiStage.IDLE,

	subscriptionCouponCodeDetails: {},
	subscriptionCouponCodeAPIStage: ApiStage.IDLE,
};

export const profileInitialState = initialState;

export const profileSlice = createSlice({
	name: 'profile',
	initialState,
	reducers: {
		toggleSubscriptionModal: (state, action) => {
			if (window.electron) {
				window.electron.ipcRenderer.send(PreloadChannels.OPEN_EXTERNAL_LINK, RENDERNET_PRICING_URL);
			} else {
				state.openSubscriptionModal = action.payload;
			}
		},
		toggleProratedPricePopUp: (state, action) => {
			state.openProratedPricePopUp = action.payload;
		},
		setOpenedMobilePricingPlans: (state, action) => {
			state.openedMobilePricingPlans = action.payload;
		},
		setGetPayLinkAPIStage: (state, action) => {
			state.getPayLinkAPIStage = action.payload;
		},

		setCurrentProfileTab: (state, action) => {
			state.currentProfileTab = action.payload;
		},

		setStudioHistoryCurrentPage: (state, action) => {
			state.studioHistoryCurrentPage = action.payload;
		},

		setUserPromptHistoryCurrentPage: (state, action) => {
			state.userPromptHistoryCurrentPage = action.payload;
		},
		setListPromptHistoryCurrentPage: (state, action) => {
			state.listPromptHistoryCurrentPage = action.payload;
		},

		setUnpublishPromptConfirmationVisible: (state, action) => {
			state.unpublishPromptConfirmationVisibility = action.payload;
		},

		setPublicProfile: (state, action) => {
			state.publicProfileDetails = action.payload;
		},
		setUserAuthenticatedStatus: (state, action) => {
			state.isUserAuthenticated = action.payload;
		},

		setUserPromptHistory: (state, action) => {
			state.userPromptHistory = action.payload;
		},

		setListPromptsHistory: (state, action) => {
			state.listPromptsHistory = action.payload;
		},
		resetGetUnpublishPromptAPIStage(state) {
			state.getUnpublishPromptAPIStage = ApiStage.IDLE;
		},
		showResetAPIKeyModal(state, action) {
			state.showResetAPIKeyModal = action.payload;
		},
		showAPISuccessModal(state, action) {
			state.showAPISuccessModal = action.payload;
		},
		setCouponCodeDetails: (state, action) => {
			const couponCodeDetails = action.payload;
			if (couponCodeDetails?.message?.length && Object.hasOwn(couponCodeDetails, 'valid')) {
				state.subscriptionCouponCodeDetails = action.payload;
			}
		},
		resetCouponCodeDetails: (state) => {
			state.subscriptionCouponCodeDetails = {};
		},
		resetCouponCodeDetailsApiStage: (state) => {
			state.subscriptionCouponCodeAPIStage = ApiStage.IDLE;
		},
	},
	extraReducers: (builder) => {
		// get public profile
		builder.addCase(getPublicProfileAsync.pending, (state) => {
			state.getPublicProfileAPIStage = ApiStage.LOADING;
		});
		builder.addCase(getPublicProfileAsync.fulfilled, (state, action) => {
			state.getPublicProfileAPIStage = ApiStage.SUCCESS;
			state.publicProfileDetails = action.payload;
		});
		builder.addCase(getPublicProfileAsync.rejected, (state, action) => {
			state.getPublicProfileAPIStage = ApiStage.ERROR;
			if (action?.error.code === RendernetErrors.USER_NOT_FOUND) {
				state.userNotFound = true;
			} else {
				sendToast(ToastType.REFRESH_ON_API_FAILURE);
			}
		});

		// get user prompt history
		builder.addCase(getUserPromptHistoryAsync.pending, (state) => {
			state.getUserPromptHistoryAPIStage = ApiStage.LOADING;
		});
		builder.addCase(getUserPromptHistoryAsync.fulfilled, (state, action) => {
			const { prompts, listSize, pageSize } = action.payload;

			state.getUserPromptHistoryAPIStage = ApiStage.SUCCESS;
			state.userPromptHistory = prompts || [];
			state.userPromptHistoryTotalPages = calculatePageNumbers(listSize, pageSize);
		});
		builder.addCase(getUserPromptHistoryAsync.rejected, (state) => {
			state.getUserPromptHistoryAPIStage = ApiStage.ERROR;
			sendToast(ToastType.REFRESH_ON_API_FAILURE);
		});

		// get list prompt history
		builder.addCase(getListPromptsHistoryAsync.pending, (state) => {
			state.getListPromptsHistoryAPIStage = ApiStage.LOADING;
		});
		builder.addCase(getListPromptsHistoryAsync.fulfilled, (state, action) => {
			const { prompts, listSize, pageSize } = action.payload;

			state.getListPromptsHistoryAPIStage = ApiStage.SUCCESS;
			state.listPromptsHistory = prompts || [];
			state.listPromptHistoryTotalPages = calculatePageNumbers(listSize, pageSize);
		});
		builder.addCase(getListPromptsHistoryAsync.rejected, (state) => {
			state.getListPromptsHistoryAPIStage = ApiStage.ERROR;
			sendToast(ToastType.REFRESH_ON_API_FAILURE);
		});

		// get studio history
		builder.addCase(getStudioHistoryAsync.pending, (state) => {
			state.getStudioHistoryAPIStage = ApiStage.LOADING;
		});
		builder.addCase(getStudioHistoryAsync.fulfilled, (state, action) => {
			const { history, listSize, pageSize } = action.payload;

			state.getStudioHistoryAPIStage = ApiStage.SUCCESS;
			state.studioHistory = history || [];
			state.studioHistoryTotalPages = calculatePageNumbers(listSize, pageSize);
		});
		builder.addCase(getStudioHistoryAsync.rejected, (state) => {
			state.getStudioHistoryAPIStage = ApiStage.ERROR;
			sendToast(ToastType.REFRESH_ON_API_FAILURE);
		});

		builder.addCase(generateCharacterAsync.rejected, (state, action) => {
			const error = action.error.code;
			if (
				error === RendernetErrors.INSUFFICIENT_CREDITS ||
				error === RendernetErrors.INSUFFICIENT_FACELOCKS
			) {
				state.openSubscriptionModal = { open: true, from: PricingModalSource.CHARACTER_GENERATOR };
				state.generateError = error;
				sendToast(ToastType.OUT_OF_CREDITS);
			}
		});

		// open add credits modal on generate studio API not-sufficient-credits error
		builder.addCase(runGenerateStudioMediaAsync.rejected, (state, action) => {
			const error = action.error.code;
			if (
				error === RendernetErrors.INSUFFICIENT_CREDITS ||
				error === RendernetErrors.INSUFFICIENT_FACELOCKS
			) {
				state.openSubscriptionModal = { open: true, from: PricingModalSource.STUDIO };
				state.generateError = error;
				sendToast(ToastType.OUT_OF_CREDITS);
			}
		});

		// open add credits modal on generate canvas API not-sufficient-credits error
		builder.addCase(runGenerateCanvasMediaAsync.rejected, (state, action) => {
			const error = action.error.code;
			if (
				error === RendernetErrors.INSUFFICIENT_CREDITS ||
				error === RendernetErrors.INSUFFICIENT_FACELOCKS
			) {
				if (window.electron) {
					window.electron.ipcRenderer.send(
						PreloadChannels.OPEN_EXTERNAL_LINK,
						RENDERNET_PRICING_URL,
					);
				} else {
					state.openSubscriptionModal = { open: true, from: PricingModalSource.CANVAS };
					state.generateError = error;
					sendToast(ToastType.OUT_OF_CREDITS);
				}
			}
		});

		builder.addCase(musicVideoUpdateScenesAsync.rejected, (state, action) => {
			const error = action.error.code;
			if (
				error === RendernetErrors.INSUFFICIENT_CREDITS ||
				error === RendernetErrors.INSUFFICIENT_FACELOCKS
			) {
				state.openSubscriptionModal = { open: true, from: PricingModalSource.STUDIO };
				state.generateError = error;
				sendToast(ToastType.OUT_OF_CREDITS);
			}
		});
		builder.addCase(getMusicVideoAsync.rejected, (state, action) => {
			const error = action.error.code;
			if (
				error === RendernetErrors.INSUFFICIENT_CREDITS ||
				error === RendernetErrors.INSUFFICIENT_FACELOCKS
			) {
				state.openSubscriptionModal = { open: true, from: PricingModalSource.STUDIO };
				state.generateError = error;
				sendToast(ToastType.OUT_OF_CREDITS);
			}
		});

		// active plans
		builder.addCase(getActivePlansAsync.pending, (state) => {
			state.getActivePlansAPIStage = ApiStage.LOADING;
		});
		builder.addCase(getActivePlansAsync.fulfilled, (state, action) => {
			state.getActivePlansAPIStage = ApiStage.SUCCESS;
			state.activePlans = action.payload;
		});
		builder.addCase(getActivePlansAsync.rejected, (state) => {
			state.getActivePlansAPIStage = ApiStage.ERROR;
			sendToast(ToastType.REFRESH_ON_API_FAILURE);
		});

		// Unpublish prompt
		builder.addCase(unpublishPromptAsync.pending, (state) => {
			state.getUnpublishPromptAPIStage = ApiStage.LOADING;
		});
		builder.addCase(unpublishPromptAsync.fulfilled, (state) => {
			state.getUnpublishPromptAPIStage = ApiStage.SUCCESS;
		});
		builder.addCase(unpublishPromptAsync.rejected, (state, action) => {
			state.getUnpublishPromptAPIStage = ApiStage.ERROR;
			const { error } = action;
			handleSomethingWentWrongErrorWithToast('API request failed', error);
		});

		//  credits
		builder.addCase(getCreditOptions.pending, (state) => {
			state.getCreditOptionsApiStage = ApiStage.LOADING;
		});
		builder.addCase(getCreditOptions.fulfilled, (state, action) => {
			state.getCreditOptionsApiStage = ApiStage.SUCCESS;
			state.creditOptions = action.payload;
		});
		builder.addCase(getCreditOptions.rejected, (state) => {
			state.getCreditOptionsApiStage = ApiStage.ERROR;
			sendToast(ToastType.GET_CREDIT_OPTIONS_FAILED);
		});

		//  subscription plans
		builder.addCase(getSubscriptionPlans.pending, (state) => {
			state.getSubscriptionPlansApiStage = ApiStage.LOADING;
		});
		builder.addCase(getSubscriptionPlans.fulfilled, (state, action) => {
			state.getSubscriptionPlansApiStage = ApiStage.SUCCESS;
			state.subscriptionPlans = action.payload;
		});
		builder.addCase(getSubscriptionPlans.rejected, (state, action) => {
			state.getSubscriptionPlansApiStage = ApiStage.ERROR;
			// TODO: UPDATE TEXT
			const { error } = action;
			handleSomethingWentWrongErrorWithToast('API request failed', error);
		});

		//  get one time pay link
		builder.addCase(getOneTimePayLinkAsync.pending, (state) => {
			state.getPayLinkAPIStage = ApiStage.LOADING;
		});
		builder.addCase(getOneTimePayLinkAsync.fulfilled, (state) => {
			state.getPayLinkAPIStage = ApiStage.SUCCESS;
		});
		builder.addCase(getOneTimePayLinkAsync.rejected, (state) => {
			state.getPayLinkAPIStage = ApiStage.ERROR;
		});

		//  get subscription pay link
		builder.addCase(getSubscriptionPayLinkAsync.pending, (state) => {
			state.getPayLinkAPIStage = ApiStage.LOADING;
		});
		builder.addCase(getSubscriptionPayLinkAsync.fulfilled, (state) => {
			state.getPayLinkAPIStage = ApiStage.SUCCESS;
		});
		builder.addCase(getSubscriptionPayLinkAsync.rejected, (state, action) => {
			state.getPayLinkAPIStage = ApiStage.ERROR;
			state.getPayLinkAPIError = action.error.code;
		});

		// validate and apply coupon code
		builder.addCase(validateAndApplyCouponCodeAsync.pending, (state) => {
			state.subscriptionCouponCodeAPIStage = ApiStage.LOADING;
			state.subscriptionCouponCodeDetails = {};
		});
		builder.addCase(validateAndApplyCouponCodeAsync.fulfilled, (state, action) => {
			state.subscriptionCouponCodeAPIStage = ApiStage.SUCCESS;
			const subscriptionCouponCodeDetails = action.payload;
			if (!subscriptionCouponCodeDetails?.valid) {
				subscriptionCouponCodeDetails.message = INVALID_COUPON_CODE_MESSAGE;
			}
			state.subscriptionCouponCodeDetails = subscriptionCouponCodeDetails;
		});
		builder.addCase(validateAndApplyCouponCodeAsync.rejected, (state, action) => {
			state.subscriptionCouponCodeAPIStage = ApiStage.ERROR;
			const { error } = action;
			if (error.code === RendernetErrors.STRIPE_INVALID_COUPON_CODE) {
				state.subscriptionCouponCodeDetails = {
					valid: false,
					message: INVALID_COUPON_CODE_MESSAGE,
				};
			} else {
				state.subscriptionCouponCodeDetails = {};
				sendToast(ToastType.FAILED_TO_APPLY_COUPON);
			}
		});

		//  get Prorated Price
		builder.addCase(getSubscriptionProratedPriceAsync.pending, (state) => {
			state.getProratedPriceApiStage = ApiStage.LOADING;
			state.openProratedPricePopUp = true;
		});
		builder.addCase(getSubscriptionProratedPriceAsync.fulfilled, (state, action) => {
			state.getProratedPriceApiStage = ApiStage.SUCCESS;
			state.proratedPrice = action.payload;
		});
		builder.addCase(getSubscriptionProratedPriceAsync.rejected, (state, action) => {
			state.getProratedPriceApiStage = ApiStage.ERROR;
			const { error } = action;
			handleSomethingWentWrongErrorWithToast('API request failed', error);
		});

		//  get manage subscriptions link
		builder.addCase(getManageSubscriptionsLinkAsync.pending, (state) => {
			state.getManageSubscriptionsLinkAPIStage = ApiStage.LOADING;
		});
		builder.addCase(getManageSubscriptionsLinkAsync.fulfilled, (state) => {
			state.getManageSubscriptionsLinkAPIStage = ApiStage.SUCCESS;
		});
		builder.addCase(getManageSubscriptionsLinkAsync.rejected, (state) => {
			state.getManageSubscriptionsLinkAPIStage = ApiStage.ERROR;
		});

		//  get API Key
		builder.addCase(generateAPIKeyAsync.pending, (state) => {
			state.getAPIKeyAPIStage = ApiStage.LOADING;
		});
		builder.addCase(generateAPIKeyAsync.fulfilled, (state, action) => {
			const { data } = action.payload;
			state.apiKey = data;
			state.getAPIKeyAPIStage = ApiStage.SUCCESS;
		});
		builder.addCase(generateAPIKeyAsync.rejected, (state, action) => {
			state.getAPIKeyAPIStage = ApiStage.ERROR;
			const { error } = action;
			handleSomethingWentWrongErrorWithToast('API request failed', error);
		});

		//  reset API Key
		builder.addCase(resetAPIKeyAsync.pending, (state) => {
			state.resetAPIKeyStage = ApiStage.LOADING;
		});
		builder.addCase(resetAPIKeyAsync.fulfilled, (state, action) => {
			const { data } = action.payload;
			state.apiKey = data;
			state.resetAPIKeyStage = ApiStage.SUCCESS;
		});
		builder.addCase(resetAPIKeyAsync.rejected, (state, action) => {
			state.resetAPIKeyStage = ApiStage.ERROR;
			const { error } = action;
			handleSomethingWentWrongErrorWithToast('API request failed', error);
		});
	},
});

export const {
	toggleSubscriptionModal,
	toggleProratedPricePopUp,
	setGetPayLinkAPIStage,
	setOpenedMobilePricingPlans,
	setCurrentProfileTab,

	setStudioHistoryCurrentPage,
	setUserPromptHistoryCurrentPage,
	setUserPromptHistory,

	setListPromptHistoryCurrentPage,
	setListPromptsHistory,

	setUnpublishPromptConfirmationVisible,

	setPublicProfile,
	setUserAuthenticatedStatus,
	resetGetUnpublishPromptAPIStage,

	showResetAPIKeyModal,
	showAPISuccessModal,

	setCouponCodeDetails,
	resetCouponCodeDetails,
	resetCouponCodeDetailsApiStage,
} = profileSlice.actions;

export const getCollapsedMobilePricingPlansSelector = (state) =>
	state.profile.openedMobilePricingPlans;
export const openSubscriptionModalSelector = (state) => state.profile.openSubscriptionModal;
export const generateErrorSelector = (state) => state.profile.generateError;
export const getManageSubscriptionsLinkAPIStageSelector = (state) =>
	state.profile.getManageSubscriptionsLinkAPIStage;

export const getPublicProfileAPIStageSelector = (state) => state.profile.getPublicProfileAPIStage;
export const getPublicProfileDetailsSelector = (state) => state.profile.publicProfileDetails;

export const getPayLinkAPIStageSelector = (state) => state.profile.getPayLinkAPIStage;
export const getPayLinkAPIErrorSelector = (state) => state.profile.getPayLinkAPIError;

export const openProratedPricePopUpSelector = (state) => state.profile.openProratedPricePopUp;
export const proratedPriceSelector = (state) => state.profile.proratedPrice;
export const getProratedPriceApiStageSelector = (state) => state.profile.getProratedPriceApiStage;

export const currentProfileTabSelector = (state) => state.profile.currentProfileTab;

export const getStudioHistoryAPIStageSelector = (state) => state.profile.getStudioHistoryAPIStage;
export const studioHistorySelector = (state) => state.profile.studioHistory;
export const studioHistoryTotalPagesSelector = (state) => state.profile.studioHistoryTotalPages;
export const studioHistoryCurrentPageSelector = (state) => state.profile.studioHistoryCurrentPage;

export const userPromptHistorySelector = (state) => state.profile.userPromptHistory;
export const userPromptHistoryTotalPagesSelector = (state) =>
	state.profile.userPromptHistoryTotalPages;
export const userPromptHistoryCurrentPageSelector = (state) =>
	state.profile.userPromptHistoryCurrentPage;

export const listPromptHistorySelector = (state) => state.profile.listPromptsHistory;
export const listPromptHistoryTotalPagesSelector = (state) =>
	state.profile.listPromptHistoryTotalPages;
export const listPromptHistoryCurrentPageSelector = (state) =>
	state.profile.listPromptHistoryCurrentPage;

export const unpublishPromptAPIStageSelector = (state) => state.profile.getUnpublishPromptAPIStage;
export const unpublishPromptConfirmationVisibilitySelector = (state) =>
	state.profile.unpublishPromptConfirmationVisibility;

export const creditOptionsSelector = (state) => state.profile.creditOptions;
export const getCreditOptionsApiStageSelector = (state) => state.profile.getCreditOptionsApiStage;

export const subscriptionPlansSelector = (state) => state.profile.subscriptionPlans;
export const getSubscriptionPlansApiStageSelector = (state) =>
	state.profile.getSubscriptionPlansApiStage;

export const subscriptionCouponDetailsSelector = (state) =>
	state.profile.subscriptionCouponCodeDetails;
export const subscriptionCouponCodeApiStageSelector = (state) =>
	state.profile.subscriptionCouponCodeAPIStage;

export const getActivePlansAPIStageSelector = (state) => state.profile.getActivePlansAPIStage;
export const activePlansSelector = (state) => state.profile.activePlans;

export const getIsUserAuthenticatedSelector = (state) => state.profile.isUserAuthenticated;

export const getAPIKeyAPIStageSelector = (state) => state.profile.getAPIKeyAPIStage;
export const apiKeySelector = (state) => state.profile.apiKey;

export const showResetAPIKeyModalSelector = (state) => state.profile.showResetAPIKeyModal;
export const showAPISuccessModalSelector = (state) => state.profile.showAPISuccessModal;

export const resetAPIKeyAPIStageSelector = (state) => state.profile.resetAPIKeyStage;
export const userNotFoundSelector = (state) => state.profile.userNotFound;

export default profileSlice.reducer;
